import {
  BottomSheet,
  BottomSheetIllustrationContent,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useEffect, useState } from "react";

import { usePandaDocConfig } from "./usePandaDocConfig";

const FAILURE_STORAGE_KEY = "pandadoc-loading-failure-timestamps";
const FAILURE_THRESHOLD = 3;
const ONE_HOUR_MS = 60 * 60 * 1000;

function recordFailure(): number {
  try {
    const now = Date.now();
    const stored = localStorage.getItem(FAILURE_STORAGE_KEY);
    let timestamps: number[] = [];
    try {
      timestamps = stored ? JSON.parse(stored) : [];
    } catch {
      timestamps = [];
    }
    const recentTimestamps = timestamps
      .filter((timestamp) => now - timestamp < ONE_HOUR_MS)
      // keep only the most recent FAILURE_THRESHOLD timestamps and not keep the older ones
      .slice(-FAILURE_THRESHOLD);
    recentTimestamps.push(now);
    localStorage.setItem(FAILURE_STORAGE_KEY, JSON.stringify(recentTimestamps));
    return recentTimestamps.length;
  } catch {
    return 0;
  }
}

interface PandaDocFailureBottomSheetProps {
  modalState: UseModalState;
  onClose?: () => void;
}

export function PandaDocFailureBottomSheet(props: PandaDocFailureBottomSheetProps) {
  const { modalState, onClose } = props;
  const { redirectToSupport } = usePandaDocConfig();
  const [showContactSupportOnLotsOfError, setShowContactSupportOnLotsOfError] = useState(false);
  const onCloseHandler = () => {
    modalState.closeModal();
    onClose?.();
  };

  useEffect(() => {
    if (modalState.modalIsOpen) {
      const count = recordFailure();
      setShowContactSupportOnLotsOfError(count >= FAILURE_THRESHOLD);
    }
  }, [modalState.modalIsOpen]);

  const shouldShowContactSupport = redirectToSupport || showContactSupportOnLotsOfError;

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={onCloseHandler} />}
      footer={
        <DialogFooter orientation="vertical">
          {shouldShowContactSupport ? (
            <>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => {
                  void openZendeskMessaging();
                }}
              >
                Contact Support
              </Button>

              <TextButton
                size="large"
                sx={{ marginTop: 1, marginBottom: 3 }}
                onClick={onCloseHandler}
              >
                Try again later
              </TextButton>
            </>
          ) : (
            <Button fullWidth variant="contained" size="large" onClick={onCloseHandler}>
              Try again later
            </Button>
          )}
        </DialogFooter>
      }
    >
      <BottomSheetIllustrationContent illustrationType="error" title="Loading Failed" />
    </BottomSheet>
  );
}
